import { type ComponentPropsWithoutRef, type JSX } from 'react'
import DOMPurify from 'dompurify'

/**
 * Shared HTML sanitizer + renderer (SE-20: stored XSS).
 *
 * Use `<SafeHtml html={...} />` (or `sanitizeHtml()`) anywhere untrusted or
 * user-authored HTML (agent notes, portal articles, inbound email, etc.) is
 * rendered via dangerouslySetInnerHTML. Keeps normal formatting, links,
 * tables, images with safe src and class/id/style; strips scripts, event
 * handlers, javascript:/vbscript:/data: script URLs, iframes, objects, forms.
 * Links get rel="noopener noreferrer"; links opening in a new tab keep
 * target="_blank". `class` and `id` are kept (unlike the email-body config in
 * ConversationTimeline) because notes/articles rely on them for styling and
 * heading anchors.
 */
const purify = typeof window !== 'undefined' ? DOMPurify(window) : null

if (purify) {
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('rel', 'noopener noreferrer')
      if (node.getAttribute('target') && node.getAttribute('target') !== '_self') {
        node.setAttribute('target', '_blank')
      } else {
        node.removeAttribute('target')
      }
    }
  })
}

const FORBID_TAGS = [
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'form', 'input', 'button', 'select', 'textarea', 'link', 'meta', 'base',
  'svg', 'math',
]

const FORBID_ATTR = ['srcdoc', 'formaction', 'action']

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return ''
  // No DOM (SSR): fall back to escaping everything.
  if (!purify) return escapeHtml(html)
  return purify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS,
    FORBID_ATTR,
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
  })
}

type SafeHtmlProps = {
  html: string | null | undefined
  as?: 'div' | 'span'
} & Omit<ComponentPropsWithoutRef<'div'>, 'dangerouslySetInnerHTML' | 'children'>

export function SafeHtml({ html, as: Tag = 'div', ...rest }: SafeHtmlProps): JSX.Element {
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
}

export default SafeHtml
